import { FormEvent, Fragment, useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Dialog, Transition } from "@headlessui/react";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import { CheckCircle2, Contact, Loader2, Printer, Search } from "lucide-react";
import { IEventApcap } from "../../@types/system";
import api from "../../service/api";
import { Button } from "../../components/Button";
import { BadgeLabel } from "../../components/badge-label";
import { openBadgePrintWindow } from "../../utils/badgePrint";
import logoFull from "../../assets/logo-full.png";
import {
  formatCpfOrCnpj,
  isValidCpfOrCnpj,
  normalizeDocumentDigits,
} from "../../lib/utils/document";

/** Altere esta lista conforme o evento */
const SECRETARIAS = [
  "AÇÃO SOCIAL",
  "ADMINISTRAÇÃO",
  "AGÊNCIA DE DESENVOLVIMENTO",
  "COMUNICAÇÃO",
  "CONTROLE INTERNO",
  "COORDENADORIA DA MULHER",
  "DISTRITAL DE BOTAFOGO",    
  "DISTRITAL DE MANGABEIRA",
  "EDUCAÇÃO",
  "ESPORTES",
  "EVENTOS",
  "FINANÇAS",
  "GABINETE",
  "GERÊNCIA E CONTRATOS",
  "GOVERNO",
  "INDÚSTRIA E COMÉRCIO",
  "ITAPREV",
  "JUVENTUDE",
  "MEIO AMBIENTE",
  "OBRAS",
  "OUVIDORIA",
  "PLANEJAMENTO",
  "PROCURADORIA",
  "RECURSOS HUMANOS",
  "SAÚDE",
  "SEGURANÇA",
  "TURISMO",
];

interface CreatedParticipant {
  id: string;
  name: string;
  position: string;
  document: string | null;
  event_id: string;
  user?: { id: string; name: string };
}

const fieldClass =
  "w-full rounded-xl border border-zinc-200 bg-white px-3.5 py-3 text-[15px] text-zinc-900 outline-none transition-[border-color,box-shadow] duration-200 placeholder:text-zinc-400 focus:border-apcap/50 focus:shadow-[0_0_0_3px_rgba(1,88,50,0.12)]";

const labelClass =
  "mb-1.5 block font-['Sora'] text-sm font-medium text-zinc-700";

export default function CadastroEvento() {
  const { eventId, userId } = useParams<{
    eventId: string;
    userId: string;
  }>();

  const [event, setEvent] = useState<IEventApcap | null>(null);
  const [loadingEvent, setLoadingEvent] = useState(true);
  const [eventError, setEventError] = useState("");

  const [document, setDocument] = useState("");
  const [name, setName] = useState("");
  const [position, setPosition] = useState("");
  const [secretaria, setSecretaria] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);

  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [createdParticipant, setCreatedParticipant] =
    useState<CreatedParticipant | null>(null);

  useEffect(() => {
    async function loadEvent() {
      if (!eventId) {
        setEventError("Evento inválido.");
        setLoadingEvent(false);
        return;
      }

      setLoadingEvent(true);
      setEventError("");

      try {
        const { data } = await api.get<IEventApcap>(
          `/events/details/${eventId}`
        );
        setEvent(data);
      } catch {
        setEventError("Não foi possível carregar o evento.");
        setEvent(null);
      } finally {
        setLoadingEvent(false);
      }
    }

    loadEvent();
  }, [eventId]);

  useEffect(() => {
    if (!userId || !document) return;

    const digits = document.replace(/\D/g, "");
    if (digits.length < 11) return;

    const timer = setTimeout(async () => {
      setLookingUp(true);
      try {
        const { data } = await api.get<{
          name: string;
          position: string;
        } | null>("/events/accreditation/lookup", {
          params: {
            document: digits,
            user_id: userId,
          },
        });

        if (data?.name) {
          setName(data.name);
        }
        if (data?.position) {
          setPosition(data.position);
        }
      } catch {
        // silencioso: ausência de match não é erro
      } finally {
        setLookingUp(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [document, userId]);

  const handlePrint = useCallback(() => {
    const divContent =
      window.document.getElementById("content-to-print")?.outerHTML || "";

    const opened = openBadgePrintWindow(divContent);
    if (!opened) {
      toast.error("Não foi possível abrir a janela de impressão");
    }
  }, []);

  const resetForm = useCallback(() => {
    setDocument("");
    setName("");
    setPosition("");
    setSecretaria("");
  }, []);

  const closeSuccessModal = useCallback(() => {
    setOpenSuccessModal(false);
    setCreatedParticipant(null);
    resetForm();
  }, [resetForm]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();

    if (!eventId || !userId) {
      toast.error("Link de cadastro inválido.");
      return;
    }

    const documentDigits = normalizeDocumentDigits(document);

    if (!isValidCpfOrCnpj(documentDigits) || !name.trim() || !position) {
      toast.info("Preencha documento, nome e cargo.");
      return;
    }

    setSubmitting(true);

    try {
      const { data } = await api.post<CreatedParticipant>(
        "/events/accreditation/public",
        {
          event_id: eventId,
          user_id: userId,
          name: name.trim(),
          document: documentDigits,
          position,
          departament: secretaria || null,
        }
      );

      setCreatedParticipant(data);
      setOpenSuccessModal(true);
    } catch (error: unknown) {
      const apiMessage =
        (
          error as { response?: { data?: { message?: string; error?: string } } }
        )?.response?.data?.message ||
        (
          error as { response?: { data?: { message?: string; error?: string } } }
        )?.response?.data?.error ||
        "Erro ao realizar cadastro.";
      toast.error(apiMessage);
    } finally {
      setSubmitting(false);
    }
  }

  if (loadingEvent) {
    return (
      <main className="relative flex min-h-screen items-center justify-center overflow-hidden bg-gradient-to-br from-zinc-950 via-[#0a2420] to-apcap-deep font-['Sora']">
        <div
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_70%_45%_at_50%_-10%,rgba(1,88,50,0.35),transparent)]"
          aria-hidden
        />
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative z-10 flex flex-col items-center gap-4 px-6 text-center"
        >
          <img
            src={logoFull}
            alt="Aprender e Capacitar Brasil"
            className="h-12 w-auto max-w-[240px] object-contain"
          />
          <div className="flex items-center gap-2 text-sm text-white/70">
            <Loader2 className="h-4 w-4 animate-spin text-emerald-400" />
            Preparando seu cadastro...
          </div>
        </motion.div>
      </main>
    );
  }

  if (eventError || !event) {
    return (
      <main className="relative flex min-h-screen items-center justify-center overflow-hidden bg-gradient-to-br from-zinc-950 via-[#0a2420] to-apcap-deep px-4 font-['Sora']">
        <div className="relative z-10 max-w-md text-center">
          <img
            src={logoFull}
            alt="Aprender e Capacitar Brasil"
            className="mx-auto mb-8 h-12 w-auto max-w-[240px] object-contain"
          />
          <p className="font-['Syne'] text-2xl font-bold text-white">
            Evento indisponível
          </p>
          <p className="mt-3 text-sm leading-relaxed text-white/65">
            {eventError || "Não encontramos este evento. Verifique o link."}
          </p>
        </div>
      </main>
    );
  }

  const positionLabel =
    createdParticipant?.position === "SERVIDOR_PUBLICO"
      ? "Servidor Público"
      : createdParticipant?.position === "PARLAMENTAR"
        ? "Parlamentar"
        : createdParticipant?.position || "";

  const formComplete =
    isValidCpfOrCnpj(document) && !!name.trim() && !!position;

  return (
    <main className="relative min-h-screen overflow-x-hidden bg-[radial-gradient(circle,_#015832_20%,_#003b43)] font-['Sora'] text-zinc-900">
      {/* Banner + máscara fixos — cobrem toda a viewport e o rodapé */}
      <div className="pointer-events-none fixed inset-0 z-0" aria-hidden>
        <img
          src={event.banner_url}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-[radial-gradient(circle,_#015832_20%,_#003b43)] opacity-80" />
      </div>

      <div className="relative z-10 mx-auto grid min-h-screen max-w-6xl lg:grid-cols-[1.05fr_0.95fr]">
        {/* Coluna marca + evento */}
        <section className="flex flex-col justify-between px-6 pb-6 pt-10 text-white sm:px-10 lg:py-14 lg:pr-8">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
          >
            <img
              src={logoFull}
              alt="Aprender e Capacitar Brasil"
              className="h-14 w-auto max-w-[min(100%,280px)] object-contain object-left drop-shadow-md sm:h-16"
              width={560}
              height={240}
              decoding="async"
            />
            <p className="mt-6 max-w-sm text-sm leading-relaxed text-white/70">
              Credenciamento oficial APCAP Brasil
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.65,
              delay: 0.12,
              ease: [0.16, 1, 0.3, 1],
            }}
            className="mt-12 lg:mt-0 lg:max-w-lg"
          >
            <p className="font-['Syne'] text-[0.7rem] font-semibold uppercase tracking-[0.22em] text-emerald-300/90">
              Você está se cadastrando em
            </p>
            <h1 className="mt-3 font-['Syne'] text-xl font-extrabold leading-[1.1] tracking-tight text-white sm:text-2xl lg:text-[2rem]">
              {event.name}
            </h1>
            <p className="mt-4 max-w-md text-base leading-relaxed text-white/75">
              Preencha seus dados para emitir seu crachá e garantir sua
              participação no evento.
            </p>

            <div className="mt-8 hidden overflow-hidden rounded-2xl border border-white/15 lg:block">
              <img
                src={event.banner_url}
                alt={event.name}
                className="aspect-[16/9] w-full object-cover"
              />
            </div>
          </motion.div>

          <p className="mt-10 text-xs text-white/40">
            © {new Date().getFullYear()} Aprender e Capacitar Brasil
          </p>
        </section>

        {/* Formulário — superfície de interação */}
        <section className="flex items-end px-4 pb-8 sm:px-8 lg:items-center lg:py-14 lg:pl-4 lg:pr-10">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.6,
              delay: 0.18,
              ease: [0.16, 1, 0.3, 1],
            }}
            className="w-full rounded-2xl border border-white/20 bg-white/95 p-6 shadow-[0_24px_48px_-16px_rgba(0,0,0,0.45)] backdrop-blur-sm sm:p-8"
          >
            <div className="mb-6 flex items-start gap-3 border-b border-zinc-100 pb-5">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-apcap-muted text-apcap">
                <Contact className="h-5 w-5" strokeWidth={2} />
              </div>
              <div>
                <h2 className="font-['Syne'] text-xl font-bold tracking-tight text-zinc-900">
                  Seus dados
                </h2>
                <p className="mt-1 text-sm text-zinc-500">
                  Informe o documento para preenchermos o que já conhecemos.
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="document" className={labelClass}>
                  Documento *
                </label>
                <div className="relative">
                  <input
                    id="document"
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    value={document}
                    onChange={(e) =>
                      setDocument(formatCpfOrCnpj(e.target.value))
                    }
                    placeholder="CPF ou CNPJ"
                    className={`${fieldClass} pr-10`}
                  />
                  <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-zinc-400">
                    {lookingUp ? (
                      <Loader2 className="h-4 w-4 animate-spin text-apcap" />
                    ) : (
                      <Search className="h-4 w-4" />
                    )}
                  </span>
                </div>
                {lookingUp && (
                  <p className="mt-1.5 text-xs text-apcap">
                    Buscando cadastro anterior...
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="name" className={labelClass}>
                  Nome completo
                </label>
                <input
                  id="name"
                  type="text"
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Como deve aparecer no crachá"
                  className={fieldClass}
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="position" className={labelClass}>
                    Cargo
                  </label>
                  <select
                    id="position"
                    value={position}
                    onChange={(e) => setPosition(e.target.value)}
                    className={fieldClass}
                  >
                    <option value="" disabled>
                      Selecione
                    </option>
                    <option value="SERVIDOR_PUBLICO">Servidor Público</option>
                    <option value="PARLAMENTAR">Parlamentar</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="secretaria" className={labelClass}>
                    Secretaria{" "}
                    <span className="font-normal text-zinc-400">
                      (opcional)
                    </span>
                  </label>
                  <select
                    id="secretaria"
                    value={secretaria}
                    onChange={(e) => setSecretaria(e.target.value)}
                    className={fieldClass}
                  >
                    <option value="">Não informar</option>
                    {SECRETARIAS.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <motion.button
                type="submit"
                disabled={submitting || !formComplete}
                whileTap={{ scale: submitting || !formComplete ? 1 : 0.98 }}
                className="mt-2 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-apcap font-['Syne'] text-[15px] font-bold text-white transition-[background-color,opacity,transform] duration-200 hover:bg-apcap-light disabled:cursor-not-allowed disabled:opacity-45"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Finalizando...
                  </>
                ) : (
                  "Finalizar cadastro"
                )}
              </motion.button>
            </form>

            <p className="mt-5 text-center text-[11px] leading-relaxed text-zinc-400">
              Ao finalizar, você poderá imprimir seu crachá imediatamente.
            </p>
          </motion.div>
        </section>
      </div>

      <Transition.Root show={openSuccessModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={closeSuccessModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-zinc-950/70 backdrop-blur-[2px] transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative w-full max-w-md transform overflow-hidden rounded-2xl bg-white text-left font-['Sora'] shadow-xl transition-all">
                  <div className="bg-gradient-to-br from-apcap to-apcap-deep px-6 pb-8 pt-7 text-white">
                    <div className="flex items-center gap-3">
                      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/15">
                        <CheckCircle2 className="h-6 w-6" strokeWidth={2} />
                      </div>
                      <div>
                        <Dialog.Title
                          as="h3"
                          className="font-['Syne'] text-xl font-bold tracking-tight"
                        >
                          Cadastro confirmado
                        </Dialog.Title>
                        <p className="mt-0.5 text-sm text-white/75">
                          Deseja imprimir o crachá agora?
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="px-6 py-6">
                    {createdParticipant && (
                      <div className="flex flex-col items-center gap-3 rounded-xl border border-zinc-100 bg-surface-subtle px-4 py-6">
                        <p className="text-center text-xs font-medium uppercase tracking-wider text-zinc-400">
                          Crachá · {positionLabel} · 60×40 mm
                        </p>
                        <BadgeLabel
                          id="content-to-print"
                          name={createdParticipant.name}
                          institution={createdParticipant.user?.name || ""}
                          qrValue={`https://app.aprenderecapacitarbrasil.com.br/scanner/${createdParticipant.id}/${eventId}`}
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-2 border-t border-zinc-100 bg-surface-subtle px-6 py-4 sm:flex-row-reverse">
                    <Button
                      type="button"
                      variant="primary"
                      onClick={handlePrint}
                      className="h-11 w-full gap-2 font-['Syne'] font-semibold sm:w-auto"
                    >
                      <Printer className="h-4 w-4" />
                      Sim, imprimir
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={closeSuccessModal}
                      className="h-11 w-full sm:w-auto"
                    >
                      Agora não
                    </Button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </main>
  );
}
